import {
  labelFromBoatFilename,
  migrateBoatDocument,
  type BoatDocument,
} from '../../sim/boat/index.ts';
import { isSafeBoatFilename } from '../../sim/boat/filename.ts';

export type BoatCatalogEntry = {
  id: string;
  label: string;
  boat: BoatDocument;
};

export const BOAT_CATALOG_PATH = '/__battleboats/boats';

const boatModules = import.meta.glob('/boats/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function idFromPath(path: string): string {
  return path.replace(/^\/boats\//, '').replace(/\.json$/i, '');
}

/** Preview/production catalog from the Vite glob. DEV prefers a live disk listing. */
export function buildBoatCatalog(modules: Record<string, unknown>): BoatCatalogEntry[] {
  const entries: BoatCatalogEntry[] = [];

  for (const [path, data] of Object.entries(modules)) {
    const boat = migrateBoatDocument(data);

    if (!boat) {
      console.warn(`Skipping ${path}: not a valid Battleboats boat document.`);
      continue;
    }

    const id = idFromPath(path);
    entries.push({
      id,
      label: boat.name.trim() || labelFromBoatFilename(id),
      boat,
    });
  }

  return entries.sort((a, b) => a.label.localeCompare(b.label));
}

export function boatCatalogFromRepoPayload(value: unknown): BoatCatalogEntry[] | null {
  if (!isRecord(value) || !Array.isArray(value.boats)) {
    return null;
  }

  const modules: Record<string, unknown> = {};

  for (const entry of value.boats) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || !isSafeBoatFilename(`${entry.id}.json`)) {
      continue;
    }

    modules[`/boats/${entry.id}.json`] = entry.data;
  }

  return buildBoatCatalog(modules);
}

const bundledBoatCatalog: BoatCatalogEntry[] = buildBoatCatalog(boatModules);

export async function fetchBoatCatalogFromRepo(): Promise<BoatCatalogEntry[] | null> {
  if (!import.meta.env.DEV) {
    return null;
  }

  try {
    const response = await fetch(BOAT_CATALOG_PATH, { cache: 'no-store' });

    if (!response.ok) {
      return null;
    }

    return boatCatalogFromRepoPayload(await response.json());
  } catch {
    return null;
  }
}

/** DEV reads `boats/` from disk. Preview and production use the files bundled at build time. */
export async function loadBoatCatalog(): Promise<readonly BoatCatalogEntry[]> {
  return (await fetchBoatCatalogFromRepo()) ?? bundledBoatCatalog;
}

export function findBoatCatalogEntry(
  id: string,
  catalog: readonly BoatCatalogEntry[],
): BoatCatalogEntry | undefined {
  return catalog.find((entry) => entry.id === id);
}
